import * as React from "react";
import type { ListingHistory } from "@/data/types";
import { formatPriceShort } from "@/lib/format";
import { Reveal, Overline } from "./primitives";

interface Props {
  history: ListingHistory;
}

export const ListingJourney: React.FC<Props> = ({ history }) => {
  if (!history?.events?.length) return null;

  return (
    <section
      className="w-full bg-ivory py-20 md:py-28"
      data-testid="listing-journey"
      aria-label="Listing journey"
    >
      <div className="mx-auto w-full max-w-3xl px-6 md:px-10">
        <Reveal>
          <Overline>Price history</Overline>
          <h2 className="mt-4 font-serif text-3xl leading-tight text-charcoal md:text-4xl">
            How the asking price moved.
          </h2>
        </Reveal>

        <Reveal delay={0.05}>
          <ol className="mt-12">
            {history.events.map((event, i) => {
              const price = formatPriceShort(event.price);
              const isLast = i === history.events.length - 1;
              return (
                <li key={i} className="relative pl-10">
                  {/* connective line */}
                  {!isLast && (
                    <span className="absolute left-[5px] top-3 h-full w-px bg-charcoal/15" />
                  )}
                  <span
                    className={`absolute left-0 top-2 h-[11px] w-[11px] rounded-full border ${
                      event.isEnd
                        ? "border-charcoal bg-charcoal"
                        : "border-brass bg-brass"
                    }`}
                  />
                  <div className="pb-10">
                    {event.daysSincePrevious != null && i > 0 && (
                      <p className="mb-3 text-xs uppercase tracking-[0.16em] text-charcoal-muted">
                        ↓ {event.daysSincePrevious} days
                      </p>
                    )}
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                      {price ? (
                        <span className="font-serif text-3xl text-charcoal md:text-4xl">
                          {price}
                        </span>
                      ) : (
                        <span className="font-serif text-2xl text-charcoal md:text-3xl">
                          {event.label}
                        </span>
                      )}
                      <span className="text-xs uppercase tracking-[0.18em] text-charcoal-muted">
                        {price ? event.label : ""}
                        {event.date ? `${price ? " · " : ""}${event.date}` : ""}
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </Reveal>

        {history.interpretation && (
          <Reveal delay={0.1}>
            <p className="mt-2 max-w-prose border-l-2 border-brass pl-6 text-base leading-relaxed text-charcoal-soft md:text-lg">
              {history.interpretation}
            </p>
          </Reveal>
        )}
      </div>
    </section>
  );
};
